import {
  forwardRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
  type SelectHTMLAttributes,
} from "react";
import { ChevronDown, Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/cn";
import { controlStyles } from "./styles";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leading?: ReactNode;
  trailing?: ReactNode;
}

/** Text input. <Input type="email" leading={<Mail className="size-4" />} /> */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ className, leading, trailing, ...props }, ref) {
  if (!leading && !trailing) return <input ref={ref} className={cn(controlStyles, "h-12", className)} {...props} />;
  return (
    <div className="relative">
      {leading && <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-ink-muted">{leading}</span>}
      <input ref={ref} className={cn(controlStyles, "h-12", leading && "pl-10", trailing && "pr-11", className)} {...props} />
      {trailing && <span className="absolute inset-y-0 right-2 flex items-center">{trailing}</span>}
    </div>
  );
});

/** Password input with a show/hide toggle. */
export const PasswordInput = forwardRef<HTMLInputElement, Omit<InputProps, "type" | "trailing">>(function PasswordInput(props, ref) {
  const [visible, setVisible] = useState(false);
  return (
    <Input
      ref={ref}
      type={visible ? "text" : "password"}
      autoComplete={props.autoComplete ?? "current-password"}
      {...props}
      trailing={
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="grid size-9 place-items-center rounded-full text-ink-muted hover:bg-fg/5 hover:text-ink"
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
        >
          {visible ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
        </button>
      }
    />
  );
});

/** Multi-line input. <Textarea rows={4} /> */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className, ...props },
  ref,
) {
  return <textarea ref={ref} className={cn(controlStyles, "min-h-[96px] py-3 leading-relaxed", className)} {...props} />;
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  placeholder?: string;
}

/** Native select (best on mobile). <Select placeholder="Choose…"><option value="a">A</option></Select> */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select({ className, placeholder, children, ...props }, ref) {
  return (
    <div className="relative">
      <select ref={ref} className={cn(controlStyles, "h-12 appearance-none pr-10", className)} {...props}>
        {placeholder !== undefined && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-ink-muted" aria-hidden />
    </div>
  );
});
